/* @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';
import { languageTagToLocalName } from 'js/lib/languageLocalized';
import { useRetracked } from 'js/lib/retracked';
import { useLocation } from 'js/lib/useRouter';

import { Button, IconButton, Popover, useTheme } from '@coursera/cds-core';
import { GlobeIcon, TranslationIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import IconButtonWrapper from 'bundles/composable-page-header/components/IconButtonWrapper';
import { DEFAULT_ICON_BUTTON_HEIGHT, DEFAULT_ICON_BUTTON_SVG_HEIGHT } from 'bundles/composable-page-header/constants';
import TranslationDropdownMenu from 'bundles/composable-page-header/legacy/translation/TranslationDropdownMenu';
import TranslationDropdownMenuWithGrouping from 'bundles/composable-page-header/legacy/translation/TranslationDropdownMenuWithGrouping';
import TranslationFeedbackDialog from 'bundles/composable-page-header/legacy/translation/TranslationFeedbackDialog';
import { useCourseLanguageQuery } from 'bundles/composable-page-header/legacy/translation/useCourseLanguageQuery';

import _t from 'i18n!nls/page';

import type { TranslationFeedback_TrackingInfo as TranslationFeedbackTrackingInfo } from '../../../../__generated__/graphql-types';

const COURSE_SLUG_REGEX = /\/learn\/([^/?]*)/;
const ITEM_ID_REGEX = /\/learn\/[^/]*\/[^/]*\/([^/?]*)/;
const PROGRAM_SLUG = /\/programs\/([^/?]*)/;
const SKILLSET_SLUG = /\/skillsets\/([^/?]*)/;
const SKILL_ID = /\/skills\/([^/?]*)/;
const useTrackingInfo = (courseId?: string, enterpriseOrgId?: string): TranslationFeedbackTrackingInfo | undefined => {
  const location = useLocation();

  if (courseId) {
    const courseSlug = location.pathname.match(COURSE_SLUG_REGEX)?.[1] ?? '';
    const itemId = location.pathname.match(ITEM_ID_REGEX)?.[1];
    return {
      inCourseTrackingInfo: {
        courseId,
        courseSlug,
        itemId,
      },
    };
  } else if (enterpriseOrgId) {
    const programSlug = location.pathname.match(PROGRAM_SLUG)?.[1];
    const skillSet = location.pathname.match(SKILLSET_SLUG)?.[1];
    const skills = location.pathname.match(SKILL_ID)?.[1];
    return {
      enterpriseTrackingInfo: {
        enterpriseOrgId,
        programSlug,
        skillSet,
        skills,
      },
    };
  }
};

const useStyles = () => {
  const { spacing } = useTheme();
  return {
    container: css`
      overflow: hidden;
    `,
    entry: css`
      &::marker {
        content: '';
      }
    `,
    feedbackButton: css`
      font-weight: normal;
      padding-top: ${spacing(8)};
      padding-bottom: ${spacing(8)};
    `,
  };
};

type Props = {
  courseId?: string;
  enterpriseOrgId?: string;
  wrapperElement?: 'li' | 'div';
  useIconButton?: boolean;
  ariaLabelPrefix?: string;
};
const TranslationDropdown = ({
  courseId,
  enterpriseOrgId,
  wrapperElement = 'li',
  ariaLabelPrefix: ariaLabelPrefixFromProps,
}: Props) => {
  const trackingInfo = useTrackingInfo(courseId, enterpriseOrgId);
  const { loading, languages, courseLanguages } = useCourseLanguageQuery(courseId);
  const retracked = useRetracked();
  const track = useTracker();

  const styles = useStyles();
  const [dropdownButton, setDropdownButton] = useState<HTMLButtonElement | null>(null);
  const isPopoverOpen = Boolean(dropdownButton);

  const [feedbackDialogOpened, setFeedbackDialogOpened] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const showTranslationDropdownWithGrouping = !!courseId;

  const locale = getUserLanguageCode();
  if (loading || !languages || !locale) return null;

  const Element = wrapperElement;

  const ariaLabelPrefix = ariaLabelPrefixFromProps ?? _t('Select language:');
  const ariaLabel = languageTagToLocalName(locale)
    ? `${ariaLabelPrefix} ${languageTagToLocalName(locale)}`
    : ariaLabelPrefix;

  const handleDropdownButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setDropdownButton(e.currentTarget);

    track('open_navigation_menu', {
      navigationType: 'language',
    });
  };

  return (
    <Element {...(wrapperElement === 'li' ? { role: 'menuitem' } : {})}>
      <IconButtonWrapper
        iconButton={
          <IconButton
            ref={buttonRef}
            variant="ghost"
            icon={<GlobeIcon size="large" color="support" />}
            aria-label={ariaLabel}
            aria-expanded={isPopoverOpen}
            tooltip={ariaLabel}
            onClick={handleDropdownButtonClick}
            data-testid="language-switcher-button"
          />
        }
        buttonSize={DEFAULT_ICON_BUTTON_HEIGHT}
        svgSize={DEFAULT_ICON_BUTTON_SVG_HEIGHT}
      />
      {isPopoverOpen && (
        <Popover
          anchorElement={dropdownButton}
          open={isPopoverOpen}
          onClose={() => {
            setDropdownButton(null);
          }}
          drawerProps={{ autoFocus: false }}
          dropdownProps={{ marginTop: '0' }}
        >
          <Popover.Body>
            {showTranslationDropdownWithGrouping ? (
              <TranslationDropdownMenuWithGrouping
                languages={languages}
                currentLanguage={locale}
                courseLanguages={courseLanguages}
              />
            ) : (
              <TranslationDropdownMenu languages={languages} currentLanguage={locale} />
            )}
          </Popover.Body>
          <Popover.Footer>
            <Button
              variant="ghost"
              size="medium"
              fullWidth
              onClick={() => {
                retracked({
                  trackingName: 'translation_feedback',
                  action: 'click',
                  trackingData: {
                    ...trackingInfo,
                    locale,
                  },
                });
                setFeedbackDialogOpened(true);
              }}
              icon={<TranslationIcon size="small" />}
              iconPosition="before"
              css={styles.feedbackButton}
            >
              {_t('Add translation feedback')}
            </Button>
          </Popover.Footer>
        </Popover>
      )}
      <TranslationFeedbackDialog
        open={feedbackDialogOpened}
        onClose={() => setFeedbackDialogOpened(false)}
        trackingInfo={trackingInfo}
      />
    </Element>
  );
};

export default TranslationDropdown;
